import { CORP_SEGMENT_ID, FIG_SEGMENT_ID, SSA_SEGMENT_ID } from '@/creditflowresearch/domain/Segment/constants';
import { MarketSegment } from '@/creditflowresearch/domain/Market/types/MarketSegment';
import type { IDictionaryIndustry } from '@/creditflowresearch/domain/vo/IDictionary';

export interface ISegmentSubindustryIds {
  id: string,
  label: MarketSegment,
  ids: string[],
}

interface ISubindustryLike {
  id: string,
  marketSegmentId: string | null,
}

export interface IIndustrySelection {
  includedIndustryIds: string[],
  includedSubindustryIds: string[],
}

const SEGMENTS = [
  { id: CORP_SEGMENT_ID, label: MarketSegment.CORP },
  { id: FIG_SEGMENT_ID, label: MarketSegment.FIG },
  { id: SSA_SEGMENT_ID, label: MarketSegment.SSA },
];

export const getSegmentSubindustryIds = (subindustries: ISubindustryLike[]): ISegmentSubindustryIds[] =>
  SEGMENTS.map((segment) => ({
    ...segment,
    ids: subindustries.filter((sub) => sub.marketSegmentId === segment.id).map((sub) => sub.id),
  }));

export const isIndustryUnrestricted = (selection: IIndustrySelection): boolean =>
  selection.includedIndustryIds.length === 0 && selection.includedSubindustryIds.length === 0;

export const getSelectedSubindustryIds = (
  industries: IDictionaryIndustry[],
  { includedIndustryIds, includedSubindustryIds }: IIndustrySelection,
): string[] => {
  const industryExpandedIds = includedIndustryIds.length > 0
    ? industries
      .filter((industry) => includedIndustryIds.includes(industry.id))
      .flatMap((industry) => industry.subindustries.map((sub) => sub.id))
    : [];

  return Array.from(new Set([...industryExpandedIds, ...(includedSubindustryIds ?? [])]));
};

export const getEffectiveSelectedSubindustryIds = (
  industries: IDictionaryIndustry[],
  selection: IIndustrySelection,
): string[] =>
  isIndustryUnrestricted(selection)
    ? industries.flatMap((industry) => industry.subindustries.map((sub) => sub.id))
    : getSelectedSubindustryIds(industries, selection);

export const isSegmentFullySelected = (segment: ISegmentSubindustryIds, selectedSubindustryIds: string[]): boolean =>
  segment.ids.length > 0 && segment.ids.every((id) => selectedSubindustryIds.includes(id));

export const getActiveSegments = (
  segments: ISegmentSubindustryIds[],
  selectedSubindustryIds: string[],
): (ISegmentSubindustryIds & { isActive: boolean })[] =>
  segments.map((segment) => ({ ...segment, isActive: isSegmentFullySelected(segment, selectedSubindustryIds) }));

export const withSelectedSubindustryIds = <T extends IIndustrySelection>(
  industries: IDictionaryIndustry[],
  filters: T,
): T & { selectedSubindustryIds: string[] } => ({
  ...filters,
  selectedSubindustryIds: getSelectedSubindustryIds(industries, filters),
});

export const withDefaultSelectedSubindustryIds = <T extends IIndustrySelection>(
  industries: IDictionaryIndustry[],
  filters: T,
): T & { selectedSubindustryIds: string[] } => ({
  ...filters,
  selectedSubindustryIds: getEffectiveSelectedSubindustryIds(industries, filters),
});
